<template>
    <div class="page points-page inventory-items-page">
        <div class="page-header">
            <h1>背包物品</h1>
            <p>
                管理可發放至會員背包的物品規格（僅可選系統營運已開放的類型／服務）
            </p>
        </div>

        <div class="content vaporwave-theme">
            <div class="toolbar">
                <div class="filters">
                    <a-space wrap size="middle">
                        <a-input
                            v-model:value="filters.keyword"
                            placeholder="物品名稱搜尋"
                            allow-clear
                            size="middle"
                            class="filter-input"
                            @press-enter="handleSearch"
                        />
                        <a-select
                            v-model:value="filters.item_type_id"
                            placeholder="背包類型"
                            allow-clear
                            size="middle"
                            class="status-select min-w-[160px]"
                            :options="itemTypeOptions"
                        />
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
                <a-button
                    v-if="canCreate"
                    type="primary"
                    size="middle"
                    class="unified-add-btn"
                    @click="openCreateModal"
                >
                    <template #icon><PlusOutlined /></template>
                    新增物品
                </a-button>
            </div>

            <DataTable
                :columns="columns"
                :data-source="list"
                :pagination="pagination"
                :loading="loading"
                @refresh="loadList"
                @change="handleTableChange"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'cover_image_url'">
                        <img
                            v-if="
                                record.cover_image_url &&
                                !imageErrorIds[record.id]
                            "
                            :src="resolveUploadAssetUrl(record.cover_image_url)"
                            alt=""
                            class="item-thumb"
                            @error="imageErrorIds[record.id] = true"
                        />
                        <div v-else class="item-thumb item-thumb-placeholder">
                            <PictureOutlined />
                        </div>
                    </template>
                    <template v-else-if="column.key === 'name'">
                        <span class="font-medium">{{ record.name }}</span>
                        <div
                            v-if="record.description"
                            class="text-gray-400 text-xs mt-1 truncate max-w-xs"
                        >
                            {{ record.description }}
                        </div>
                    </template>
                    <template v-else-if="column.key === 'type'">
                        <a-tag :color="typeColor(record.type)">
                            {{ typeLabel(record.type) }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'rarity'">
                        <a-tag :color="rarityTagColor(record.rarity)">
                            {{
                                record.rarity_display ??
                                rarityLabel(record.rarity)
                            }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'bind_type'">
                        <a-tag
                            :color="
                                record.bind_type === 'bound' ? 'orange' : 'blue'
                            "
                        >
                            {{
                                record.bind_type === 'bound'
                                    ? '帳號綁定'
                                    : '可轉移'
                            }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'item_type'">
                        <a-tag v-if="getItemTypeLabel(record)" color="purple">
                            {{ getItemTypeLabel(record) }}
                        </a-tag>
                        <span v-else>—</span>
                    </template>
                    <template v-else-if="column.key === 'service_code'">
                        {{ getServiceLabel(record.service_code) }}
                    </template>
                    <template v-else-if="column.key === 'expires_in_days'">
                        {{
                            record.expires_in_days != null
                                ? `${record.expires_in_days} 天`
                                : '永不到期'
                        }}
                    </template>
                    <template v-else-if="column.key === 'actions'">
                        <a-space size="small" class="row-actions">
                            <a-button
                                v-if="canEdit"
                                size="small"
                                class="btn-edit"
                                @click="openEditModal(record)"
                            >
                                編輯
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="btn-delete"
                                @click="confirmDelete(record)"
                            >
                                刪除
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 新增/編輯 Modal -->
        <a-modal
            v-model:open="formVisible"
            :title="formData.id ? '編輯物品' : '新增物品'"
            :width="600"
            :confirm-loading="formLoading"
            ok-text="儲存"
            cancel-text="取消"
            @ok="submitForm"
            @cancel="resetForm"
        >
            <a-form
                ref="formRef"
                :model="formData"
                :rules="formRules"
                layout="vertical"
            >
                <a-form-item label="物品名稱" name="name">
                    <a-input
                        v-model:value="formData.name"
                        placeholder="請輸入物品名稱"
                        :maxlength="150"
                    />
                </a-form-item>
                <a-form-item label="說明" name="description">
                    <a-textarea
                        v-model:value="formData.description"
                        placeholder="物品說明（選填）"
                        :rows="3"
                    />
                </a-form-item>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item name="item_type_id">
                            <template #label>
                                <span class="form-label-with-tip">
                                    背包類型
                                    <a-tooltip title="決定前台名稱、分類與按鈕（報名／預約／兌換）">
                                        <QuestionCircleOutlined class="form-label-tip" />
                                    </a-tooltip>
                                </span>
                            </template>
                            <a-select
                                v-model:value="formData.item_type_id"
                                placeholder="僅可選系統已開放類型"
                                allow-clear
                                :options="itemTypeFormOptions"
                                @change="onItemTypeChange"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item
                            name="service_code"
                            :required="selectedTypeRequiresService"
                        >
                            <template #label>
                                <span class="form-label-with-tip">
                                    對應服務
                                    <a-tooltip :title="serviceTip">
                                        <QuestionCircleOutlined class="form-label-tip" />
                                    </a-tooltip>
                                </span>
                            </template>
                            <a-select
                                v-model:value="formData.service_code"
                                :placeholder="
                                    selectedTypeRequiresService
                                        ? '此類型必須指定服務'
                                        : '選填（僅已開放服務）'
                                "
                                allow-clear
                                :options="serviceOptions"
                                :disabled="!formData.item_type_id"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-form-item v-if="formData.service_code">
                    <template #label>
                        <span class="form-label-with-tip">
                            服務參數
                            <a-tooltip
                                title="選填。JSON 物件，發放後快照並在查卷時送給對應系統。例：{&quot;level&quot;:&quot;senior&quot;}"
                            >
                                <QuestionCircleOutlined class="form-label-tip" />
                            </a-tooltip>
                        </span>
                    </template>
                    <a-textarea
                        v-model:value="serviceParamsJson"
                        placeholder='選填，例如 {"level":"senior"}'
                        :rows="3"
                        :status="serviceParamsJsonError ? 'error' : undefined"
                    />
                    <div v-if="serviceParamsJsonError" class="service-params-error">
                        {{ serviceParamsJsonError }}
                    </div>
                </a-form-item>
                <a-row :gutter="16">
                    <a-col :span="8">
                        <a-form-item>
                            <template #label>
                                <span class="form-label-with-tip">
                                    履約形態
                                    <a-tooltip :title="fulfillmentHint">
                                        <QuestionCircleOutlined class="form-label-tip" />
                                    </a-tooltip>
                                </span>
                            </template>
                            <div class="fulfillment-auto">
                                <a-tag :color="typeColor(formData.type)">
                                    {{ typeLabel(formData.type) }}
                                </a-tag>
                                <a-checkbox
                                    v-model:checked="isPhysicalShip"
                                    @change="onPhysicalShipChange"
                                >
                                    實體出貨／兌物
                                </a-checkbox>
                            </div>
                        </a-form-item>
                    </a-col>
                    <a-col :span="8">
                        <a-form-item label="稀有度" name="rarity">
                            <a-select
                                v-model:value="formData.rarity"
                                :options="rarityOptions"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="8">
                        <a-form-item label="綁定類型" name="bind_type">
                            <a-select
                                v-model:value="formData.bind_type"
                                :options="bindTypeOptions"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="到期天數" name="expires_in_days">
                            <a-input-number
                                v-model:value="formData.expires_in_days"
                                placeholder="留空 = 永不到期"
                                :min="1"
                                style="width: 100%"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="新物品標記天數" name="is_new_days">
                            <a-input-number
                                v-model:value="formData.is_new_days"
                                :min="0"
                                style="width: 100%"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="堆疊上限" name="stack_limit">
                            <a-input-number
                                v-model:value="formData.stack_limit"
                                :min="1"
                                :max="9999"
                                style="width: 100%"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-form-item label="封面圖片" name="cover_image_url">
                    <div v-if="imagePreviewUrl" class="image-preview-wrap">
                        <img
                            :src="imagePreviewUrl"
                            class="image-preview"
                            alt="封面預覽"
                            @error="imagePreviewUrl = ''"
                        />
                    </div>
                    <div class="image-input-group">
                        <a-input
                            v-model:value="formData.cover_image_url"
                            placeholder="貼上圖片 URL（留空清除圖片）"
                            allow-clear
                            class="flex-1"
                            @input="handleCoverUrlInput"
                        />
                        <span class="image-or-divider">或</span>
                        <a-upload
                            :show-upload-list="false"
                            :before-upload="() => false"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            :disabled="imageProcessing"
                            @change="handleImageFileChange"
                        >
                            <a-button
                                class="unified-search-btn"
                                :loading="imageProcessing"
                            >
                                <PictureOutlined />
                                {{ coverImageFile ? '重新選擇' : '上傳圖片' }}
                            </a-button>
                        </a-upload>
                        <a-button
                            v-if="coverImageFile"
                            danger
                            size="small"
                            @click="clearImageFile"
                        >
                            清除
                        </a-button>
                    </div>
                    <div v-if="coverImageFile" class="image-file-hint">
                        已選擇：{{ coverImageFile.name }}（{{
                            (coverImageFile.size / 1024).toFixed(0)
                        }}
                        KB）
                    </div>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 刪除確認 -->
        <a-modal
            v-model:open="deleteVisible"
            title="刪除物品"
            :width="480"
            :confirm-loading="deleteLoading"
            ok-text="確認刪除"
            ok-type="danger"
            cancel-text="取消"
            class="inventory-delete-modal"
            @ok="handleDelete"
            @cancel="closeDeleteModal"
        >
            <div v-if="deleteTarget" class="delete-modal-body">
                <div class="delete-item-preview">
                    <div class="delete-item-thumb-wrap">
                        <img
                            v-if="
                                deleteTarget.cover_image_url &&
                                !deleteImageError
                            "
                            :src="
                                resolveUploadAssetUrl(
                                    deleteTarget.cover_image_url
                                )
                            "
                            alt=""
                            class="delete-item-thumb"
                            @error="deleteImageError = true"
                        />
                        <div
                            v-else
                            class="delete-item-thumb delete-item-thumb-placeholder"
                        >
                            <PictureOutlined />
                        </div>
                    </div>
                    <div class="delete-item-info">
                        <div class="delete-item-name">
                            {{ deleteTarget.name }}
                        </div>
                        <div class="delete-item-meta">
                            <a-tag
                                v-if="deleteTarget.item_type_display"
                                color="blue"
                                size="small"
                            >
                                {{ deleteTarget.item_type_display }}
                            </a-tag>
                            <a-tag
                                size="small"
                                :color="rarityTagColor(deleteTarget.rarity)"
                            >
                                {{
                                    deleteTarget.rarity_display ??
                                    rarityLabel(deleteTarget.rarity)
                                }}
                            </a-tag>
                        </div>
                    </div>
                </div>
                <p class="delete-confirm-hint">
                    刪除後無法復原，請確認是否繼續。
                </p>
                <a-alert
                    v-if="deleteError"
                    type="error"
                    show-icon
                    class="delete-modal-alert"
                    :message="deleteError"
                />
                <a-alert
                    v-else
                    type="warning"
                    show-icon
                    class="delete-modal-alert"
                    message="若此物品已綁定商品，需先在商品設定中解除關聯後才能刪除。"
                />
            </div>
        </a-modal>

    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import {
    PlusOutlined,
    PictureOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    getInventoryItems,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem
} from '@/api/inventory-items';
import {
    getInventoryItemTypes,
    getInventoryItemServices
} from '@/api/inventoryItemTypes';
import { prepareImageForUpload } from '@/utils/imageUpload';
import { resolveUploadAssetUrl } from '@/utils/resolveUploadAssetUrl';
import {
    rarityLabel,
    rarityTagColor,
    itemTypeDisplayName,
    INVENTORY_RARITY_LABELS
} from '@/constants/inventory';

const adminStore = useAdminInfo();

const canCreate = computed(() => adminStore.hasPermissionCode('190802'));
const canEdit = computed(() => adminStore.hasPermissionCode('190803'));
const canDelete = computed(() => adminStore.hasPermissionCode('190804'));

// List state
const list = ref([]);
const loading = ref(false);
const imageErrorIds = ref({});
const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0,
    showSizeChanger: true,
    showTotal: (t) => `共 ${t} 筆`
});

// Filter state
const filters = reactive({ keyword: '', item_type_id: null });

// Form state (declared early — options computed depend on it)
const formVisible = ref(false);
const formLoading = ref(false);
const formRef = ref(null);
const formData = reactive({
    id: null,
    name: '',
    description: '',
    type: 'virtual',
    item_type_id: null,
    service_code: null,
    service_params: null,
    rarity: 'white',
    bind_type: 'bound',
    cover_image_url: '',
    expires_in_days: null,
    is_new_days: 7,
    stack_limit: 99
});
const coverImageFile = ref(null);
const imagePreviewUrl = ref('');
const imageProcessing = ref(false);
/** 履約覆寫：勾選後固定 physical，取消則回推背包類型建議 */
const isPhysicalShip = ref(false);
/** 服務參數 JSON 字串（選填） */
const serviceParamsJson = ref('');
const serviceParamsJsonError = ref('');

const formatServiceParamsJson = (params) => {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
        return '';
    }
    return JSON.stringify(params, null, 2);
};

/** @returns {object|null|false} false = 解析失敗 */
const parseServiceParamsJson = () => {
    serviceParamsJsonError.value = '';
    const raw = String(serviceParamsJson.value || '').trim();
    if (!raw) {
        formData.service_params = null;
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (
            parsed === null ||
            typeof parsed !== 'object' ||
            Array.isArray(parsed)
        ) {
            serviceParamsJsonError.value = '請輸入 JSON 物件，例如 {"level":"senior"}';
            return false;
        }
        formData.service_params = Object.keys(parsed).length ? parsed : null;
        return formData.service_params;
    } catch {
        serviceParamsJsonError.value = 'JSON 格式不正確';
        return false;
    }
};
const formRules = {
    name: [{ required: true, message: '請輸入物品名稱' }],
    rarity: [{ required: true, message: '請選擇稀有度' }],
    bind_type: [{ required: true, message: '請選擇綁定類型' }]
};

// Item type / service options
const itemTypeList = ref([]);
const serviceList = ref([]);

const itemTypeOptions = computed(() =>
    itemTypeList.value.map((t) => ({
        value: t.id,
        label:
            t.is_active === false
                ? `${t.display_name}（已關閉）`
                : t.display_name
    }))
);

/** 表單：僅開放類型；編輯時保留目前已選的關閉類型 */
const itemTypeFormOptions = computed(() => {
    const currentId = formData.item_type_id;
    return itemTypeList.value
        .filter((t) => t.is_active !== false || t.id === currentId)
        .map((t) => ({
            value: t.id,
            label:
                t.is_active === false
                    ? `${t.display_name}（已關閉）`
                    : t.display_name
        }));
});

const serviceOptions = computed(() => {
    const current = formData.service_code;
    return serviceList.value
        .filter((s) => s.is_active !== false || s.code === current)
        .map((s) => ({
            value: s.code,
            label:
                s.is_active === false
                    ? `${s.display_name}（已關閉）`
                    : s.display_name
        }));
});

const selectedTypeMeta = computed(
    () => itemTypeList.value.find((t) => t.id === formData.item_type_id) || null
);

const selectedTypeRequiresService = computed(
    () => !!selectedTypeMeta.value?.requires_service
);

/**
 * 由背包類型推履約形態：之後前台／訂單可逐步改看 primary_action，
 * 履約形態仍保留給訂單結案等舊邏輯。
 */
/** 與 API InventoryItemType::suggestFulfillmentType 對齊 */
const suggestFulfillmentType = (meta) => {
    if (!meta) return 'virtual';
    const code = String(meta.code || '').trim();
    const action = String(meta.primary_action || 'view').trim();
    if (
        action === 'enroll' ||
        action === 'redeem' ||
        action === 'book' ||
        action === 'checkout_discount'
    ) {
        return 'ticket';
    }
    if (
        ['course_ticket', 'redeem_ticket', 'service_ticket', 'discount_coupon'].includes(
            code
        )
    ) {
        return 'ticket';
    }
    return 'virtual';
};

const serviceTip = computed(() =>
    selectedTypeRequiresService.value
        ? '此背包類型必須指定服務'
        : '選填；僅已開放服務可選'
);

const fulfillmentHint = computed(() => {
    if (isPhysicalShip.value) {
        return '已標為實體出貨／兌物。取消勾選後會依背包類型自動帶回履約形態。';
    }
    const suggested = suggestFulfillmentType(selectedTypeMeta.value);
    if (suggested === 'ticket') {
        return '依背包類型自動為「憑證票券」（使用後才結案）。若要寄送實體請勾選「實體出貨／兌物」。';
    }
    if (suggested === 'virtual') {
        return '依背包類型自動為「虛寶道具」（發放即可結案）。若要寄送實體請勾選「實體出貨／兌物」。';
    }
    return '履約由背包類型自動決定；僅實體出貨需另外勾選。';
});

const syncFulfillmentFromBagType = () => {
    if (isPhysicalShip.value) {
        formData.type = 'physical';
        return;
    }
    formData.type = suggestFulfillmentType(selectedTypeMeta.value) || 'virtual';
};

const onPhysicalShipChange = () => {
    syncFulfillmentFromBagType();
};

const loadItemTypes = async () => {
    try {
        const [typesRes, servicesRes] = await Promise.all([
            getInventoryItemTypes(),
            getInventoryItemServices()
        ]);
        if (typesRes.result && Array.isArray(typesRes.data)) {
            itemTypeList.value = typesRes.data;
        }
        if (servicesRes.result && Array.isArray(servicesRes.data)) {
            serviceList.value = servicesRes.data;
        }
    } catch {
        // non-blocking
    }
};

const onItemTypeChange = () => {
    if (!formData.item_type_id) {
        formData.service_code = null;
    }
    syncFulfillmentFromBagType();
};

const loadList = async () => {
    loading.value = true;
    try {
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };
        if (filters.keyword) params.keyword = filters.keyword;
        if (filters.item_type_id) params.item_type_id = filters.item_type_id;
        const res = await getInventoryItems(params);
        if (res.result && res.data) {
            list.value = res.data.data ?? [];
            pagination.total = res.data.total ?? 0;
        } else {
            list.value = [];
            pagination.total = 0;
        }
    } catch {
        message.error('載入物品定義失敗');
        list.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
    pagination.current = 1;
    loadList();
};
const handleReset = () => {
    filters.keyword = '';
    filters.item_type_id = null;
    pagination.current = 1;
    loadList();
};
const handleTableChange = ({ pagination: pag } = {}) => {
    if (!pag?.current) return;
    pagination.current = pag.current;
    pagination.pageSize = pag.pageSize;
    loadList();
};

const openCreateModal = () => {
    resetForm();
    formVisible.value = true;
};

const openEditModal = (record) => {
    isPhysicalShip.value = record.type === 'physical';
    Object.assign(formData, {
        id: record.id,
        name: record.name,
        description: record.description ?? '',
        type: record.type ?? 'virtual',
        item_type_id: record.item_type_id ?? null,
        service_code: record.service_code ?? null,
        service_params: record.service_params ?? null,
        rarity: record.rarity,
        bind_type: record.bind_type,
        cover_image_url: record.cover_image_url ?? '',
        expires_in_days: record.expires_in_days ?? null,
        is_new_days: record.is_new_days ?? 7,
        stack_limit: record.stack_limit ?? 99
    });
    serviceParamsJson.value = formatServiceParamsJson(record.service_params);
    serviceParamsJsonError.value = '';
    if (!isPhysicalShip.value) {
        syncFulfillmentFromBagType();
    }
    coverImageFile.value = null;
    imagePreviewUrl.value = resolveUploadAssetUrl(record.cover_image_url ?? '');
    formVisible.value = true;
};

const resetForm = () => {
    isPhysicalShip.value = false;
    serviceParamsJson.value = '';
    serviceParamsJsonError.value = '';
    Object.assign(formData, {
        id: null,
        name: '',
        description: '',
        type: 'virtual',
        item_type_id: null,
        service_code: null,
        service_params: null,
        rarity: 'white',
        bind_type: 'bound',
        cover_image_url: '',
        expires_in_days: null,
        is_new_days: 7,
        stack_limit: 99
    });
    coverImageFile.value = null;
    imagePreviewUrl.value = '';
    formRef.value?.resetFields();
    formVisible.value = false;
};

const handleImageFileChange = async ({ file }) => {
    const raw = file.originFileObj ?? file;
    if (!raw || imageProcessing.value) return;

    imageProcessing.value = true;
    try {
        const prepared = await prepareImageForUpload(raw, { message });
        coverImageFile.value = prepared;
        formData.cover_image_url = '';
        imagePreviewUrl.value = URL.createObjectURL(prepared);
    } catch (err) {
        coverImageFile.value = null;
        message.error(err.message || '圖片處理失敗');
    } finally {
        imageProcessing.value = false;
    }
};

const clearImageFile = () => {
    coverImageFile.value = null;
    imagePreviewUrl.value = resolveUploadAssetUrl(
        formData.cover_image_url || ''
    );
};

const handleCoverUrlInput = (e) => {
    if (coverImageFile.value) {
        coverImageFile.value = null;
    }
    imagePreviewUrl.value = resolveUploadAssetUrl(e.target.value);
};

const submitForm = async () => {
    try {
        await formRef.value.validate();
    } catch {
        return;
    }
    if (selectedTypeRequiresService.value && !formData.service_code) {
        message.error('此物品類型必須指定對應服務');
        return;
    }
    if (formData.service_code && parseServiceParamsJson() === false) {
        message.error(serviceParamsJsonError.value || '服務參數 JSON 不正確');
        return;
    }
    if (!formData.service_code) {
        formData.service_params = null;
        serviceParamsJson.value = '';
    }
    formLoading.value = true;
    try {
        let payload;
        if (coverImageFile.value) {
            const fd = new FormData();
            fd.append('name', formData.name);
            if (formData.description)
                fd.append('description', formData.description);
            fd.append('type', formData.type);
            if (formData.item_type_id)
                fd.append('item_type_id', formData.item_type_id);
            if (formData.service_code)
                fd.append('service_code', formData.service_code);
            fd.append(
                'service_params',
                formData.service_params
                    ? JSON.stringify(formData.service_params)
                    : ''
            );
            fd.append('rarity', formData.rarity);
            fd.append('bind_type', formData.bind_type);
            fd.append('cover_image', coverImageFile.value);
            if (formData.expires_in_days)
                fd.append('expires_in_days', formData.expires_in_days);
            fd.append('is_new_days', formData.is_new_days);
            fd.append('stack_limit', formData.stack_limit);
            payload = fd;
        } else {
            payload = {
                name: formData.name,
                description: formData.description || undefined,
                type: formData.type,
                item_type_id: formData.item_type_id || undefined,
                service_code: formData.service_code || null,
                service_params: formData.service_code
                    ? formData.service_params
                    : null,
                rarity: formData.rarity,
                bind_type: formData.bind_type,
                cover_image_url: formData.cover_image_url || '',
                expires_in_days: formData.expires_in_days || undefined,
                is_new_days: formData.is_new_days,
                stack_limit: formData.stack_limit
            };
        }
        if (formData.id) {
            await updateInventoryItem(formData.id, payload);
            message.success('更新成功');
        } else {
            await createInventoryItem(payload);
            message.success('建立成功');
        }
        resetForm();
        loadList();
    } catch (err) {
        const status = err?.response?.status;
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            (status === 413
                ? '上傳檔案過大，請壓縮圖片後再試（建議小於 2MB）'
                : formData.id
                  ? '更新失敗'
                  : '建立失敗');
        message.error(msg);
    } finally {
        formLoading.value = false;
    }
};

// Delete state
const deleteVisible = ref(false);
const deleteLoading = ref(false);
const deleteTarget = ref(null);
const deleteError = ref('');
const deleteImageError = ref(false);

const closeDeleteModal = () => {
    deleteVisible.value = false;
    deleteTarget.value = null;
    deleteError.value = '';
    deleteImageError.value = false;
};

const confirmDelete = (record) => {
    deleteTarget.value = record;
    deleteError.value = '';
    deleteImageError.value = false;
    deleteVisible.value = true;
};

const handleDelete = async () => {
    deleteLoading.value = true;
    deleteError.value = '';
    try {
        await deleteInventoryItem(deleteTarget.value.id);
        message.success('刪除成功');
        closeDeleteModal();
        loadList();
    } catch (err) {
        deleteError.value =
            err?.response?.data?.message ??
            err?.message ??
            '刪除失敗，請稍後再試';
        throw err;
    } finally {
        deleteLoading.value = false;
    }
};

// Columns
const columns = computed(() => {
    const cols = [
        {
            title: '圖片',
            key: 'cover_image_url',
            dataIndex: 'cover_image_url',
            width: 70,
            align: 'center'
        },
        {
            title: '名稱',
            key: 'name',
            dataIndex: 'name',
            width: 200,
            ellipsis: true
        },
        {
            title: '背包類型',
            key: 'item_type',
            dataIndex: 'item_type',
            width: 110
        },
        {
            title: '履約',
            key: 'type',
            dataIndex: 'type',
            width: 90,
            align: 'center'
        },
        {
            title: '稀有度',
            key: 'rarity',
            dataIndex: 'rarity',
            width: 90,
            align: 'center'
        },
        {
            title: '綁定',
            key: 'bind_type',
            dataIndex: 'bind_type',
            width: 100,
            align: 'center'
        },
        {
            title: '對應服務',
            key: 'service_code',
            dataIndex: 'service_code',
            width: 120
        },
        {
            title: '到期天數',
            key: 'expires_in_days',
            dataIndex: 'expires_in_days',
            width: 100,
            align: 'center'
        },
        {
            title: '堆疊上限',
            key: 'stack_limit',
            dataIndex: 'stack_limit',
            width: 90,
            align: 'center'
        }
    ];
    if (canEdit.value || canDelete.value) {
        cols.push({
            title: '操作',
            key: 'actions',
            width: 120,
            fixed: 'right'
        });
    }
    return cols;
});

// Options
const rarityOptions = Object.entries(INVENTORY_RARITY_LABELS).map(
    ([value, label]) => ({
        value,
        label
    })
);
const bindTypeOptions = [
    { value: 'bound', label: '帳號綁定' },
    { value: 'transferable', label: '可轉移' }
];

const typeColor = (type) =>
    ({ virtual: 'default', ticket: 'blue', physical: 'green' })[type] ??
    'default';
const typeLabel = (type) =>
    ({ virtual: '虛寶道具', ticket: '憑證票券', physical: '實體兌物' })[type] ??
    type;
const getItemTypeLabel = (record) =>
    itemTypeDisplayName(record, itemTypeOptions.value);

const getServiceLabel = (code) => {
    if (!code) return '—';
    const found = serviceList.value.find((s) => s.code === code);
    return found?.display_name || code;
};

onMounted(() => {
    loadItemTypes();
    loadList();
});
</script>

<style scoped>
.item-thumb {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 6px;
}
.item-thumb-placeholder {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 6px;
    color: #bbb;
}
/* 封面圖片 */
.image-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.image-or-divider {
    color: var(--neon-purple, #2a4a7a);
    font-size: 12px;
    white-space: nowrap;
    flex-shrink: 0;
}
.image-preview-wrap {
    margin-bottom: 8px;
}
.image-preview {
    max-width: 120px;
    max-height: 80px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid rgba(42, 74, 122, 0.4);
}
.image-file-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #2a4a7a;
    font-family: 'Courier New', monospace;
}

.delete-modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.delete-item-preview {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    background: linear-gradient(
        90deg,
        rgba(42, 74, 122, 0.08),
        rgba(138, 175, 255, 0.06)
    );
    border: 1px solid rgba(42, 74, 122, 0.22);
    border-radius: 10px;
}

.delete-item-thumb-wrap {
    flex-shrink: 0;
}

.delete-item-thumb {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid rgba(42, 74, 122, 0.3);
    display: block;
}

.delete-item-thumb-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(42, 74, 122, 0.1);
    color: #2a4a7a;
    font-size: 22px;
}

.delete-item-info {
    flex: 1;
    min-width: 0;
}

.delete-item-name {
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    margin-bottom: 8px;
    line-height: 1.35;
    word-break: break-word;
}

.delete-item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.delete-confirm-hint {
    margin: 0;
    font-size: 14px;
    color: #4b5563;
    line-height: 1.5;
}

.delete-modal-alert {
    margin: 0;
}

.form-label-with-tip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.form-label-tip {
    color: rgba(0, 0, 0, 0.35);
    font-size: 13px;
    cursor: help;
}

.form-label-tip:hover {
    color: #2a4a7a;
}

.fulfillment-auto {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    min-height: 32px;
}

.service-params-error {
    margin-top: 4px;
    color: #cf1322;
    font-size: 12px;
}
</style>
